import React from 'react';
import styled from 'styled-components';

interface AvailableOutlet {
    outlet: string;
    vendorCount: number;
}

interface Props {
    availableOutlets: AvailableOutlet[];
    onClose: () => void;
    onSelectOutlet: (outlet: AvailableOutlet) => void;
    isLoading?: boolean;
}

export function AddOutletsModal({ availableOutlets, onClose, onSelectOutlet, isLoading }: Props) {
    return (
        <ModalOverlay>
            <ModalContent>
                <ModalHeader>
                    <h2>Add Outlets</h2>
                    <button className="close-btn" onClick={onClose} type="button">
                        ×
                    </button>
                </ModalHeader>

                <ModalBody>
                    {isLoading ? (
                        <LoadingState>
                            <p>Loading available outlets...</p>
                        </LoadingState>
                    ) : availableOutlets.length === 0 ? (
                        <EmptyState>
                            <p>
                                <strong>No additional outlets available</strong>
                            </p>
                            <p>All outlets are already assigned to this product group.</p>
                        </EmptyState>
                    ) : (
                        <OutletsList>
                            <div className="list-header">
                                <h4>Available Outlets</h4>
                                <p className="help-text">
                                    Select outlets to add to this product group. All vendors for each outlet will be
                                    added.
                                </p>
                            </div>

                            {availableOutlets.map((availableOutlet) => (
                                <OutletItem key={availableOutlet.outlet}>
                                    <OutletInfo>
                                        <span className="outlet-name">{availableOutlet.outlet}</span>
                                        <span className="vendor-count">{availableOutlet.vendorCount} vendor(s)</span>
                                    </OutletInfo>
                                    <AddButton onClick={() => onSelectOutlet(availableOutlet)} type="button">
                                        Add Outlet
                                    </AddButton>
                                </OutletItem>
                            ))}
                        </OutletsList>
                    )}
                </ModalBody>

                <ModalFooter>
                    <button className="wfp-btn wfp-btn--secondary" onClick={onClose} type="button">
                        Close
                    </button>
                </ModalFooter>
            </ModalContent>
        </ModalOverlay>
    );
}

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e5e5;

    h2 {
        margin: 0;
        color: #333;
        font-size: 1.5rem;
    }

    .close-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: #666;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            color: #333;
        }
    }
`;

const ModalBody = styled.div`
    padding: 1.5rem 2rem;
    overflow-y: auto;
    flex: 1;
`;

const LoadingState = styled.div`
    text-align: center;
    padding: 2rem 0;
    color: #666;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 2rem 0;
    color: #666;

    p:first-child {
        font-weight: 600;
        margin-bottom: 0.5rem;
    }

    p:last-child {
        margin: 0;
        font-size: 0.9rem;
    }
`;

const OutletsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .list-header {
        margin-bottom: 1rem;

        h4 {
            margin: 0 0 0.5rem 0;
            color: #333;
            font-size: 1.1rem;
        }

        .help-text {
            margin: 0;
            color: #666;
            font-size: 0.9rem;
        }
    }
`;

const OutletItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    background: #f8f9fa;
    transition: background-color 0.2s;

    &:hover {
        background: #e9ecef;
    }
`;

const OutletInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .outlet-name {
        font-weight: 500;
        color: #333;
        font-size: 1rem;
    }

    .vendor-count {
        font-size: 0.85rem;
        color: #666;
    }
`;

const AddButton = styled.button`
    background: #28a745;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background-color 0.2s;

    &:hover {
        background: #218838;
    }

    &:focus {
        outline: 2px solid #28a745;
        outline-offset: 2px;
    }
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 1.5rem 2rem;
    border-top: 1px solid #e5e5e5;

    .wfp-btn {
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-size: 0.875rem;
        transition: background-color 0.2s;

        &.wfp-btn--secondary {
            background: #6c757d;
            color: white;

            &:hover {
                background: #5a6268;
            }
        }
    }
`;
